import { useCallback, useEffect, useState } from 'react'

import {
  readSidebarExpandOnHover,
  readSidebarExpanded,
  SIDEBAR_EXPAND_ON_HOVER_STORAGE_KEY,
  SIDEBAR_EXPANDED_STORAGE_KEY,
  SIDEBAR_PREFS_CHANGED_EVENT,
  writeSidebarExpandOnHover,
  writeSidebarExpanded,
} from './sidebarExpandedStorage'

const DESKTOP_MIN_WIDTH = 1024

function isDesktopViewport(): boolean {
  if (typeof window === 'undefined') return true
  if (typeof window.matchMedia !== 'function') return true
  return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches
}

export function useSidebarPrefs() {
  const [expanded, setExpandedState] = useState(() => readSidebarExpanded())
  const [expandOnHover, setExpandOnHoverState] = useState(() => readSidebarExpandOnHover())
  const [hoverPeek, setHoverPeek] = useState(false)
  const [canExpand, setCanExpand] = useState(isDesktopViewport)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`)
    const syncViewport = () => {
      setCanExpand(media.matches)
      if (!media.matches) setHoverPeek(false)
    }
    syncViewport()
    media.addEventListener('change', syncViewport)
    return () => media.removeEventListener('change', syncViewport)
  }, [])

  useEffect(() => {
    const syncFromStorage = (key?: string) => {
      if (!key || key === SIDEBAR_EXPANDED_STORAGE_KEY) {
        setExpandedState(readSidebarExpanded())
      }
      if (!key || key === SIDEBAR_EXPAND_ON_HOVER_STORAGE_KEY) {
        setExpandOnHoverState(readSidebarExpandOnHover())
      }
    }

    const onStorage = (event: StorageEvent) => {
      syncFromStorage(event.key ?? undefined)
    }

    const onPrefsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ key?: string }>).detail
      syncFromStorage(detail?.key)
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener(SIDEBAR_PREFS_CHANGED_EVENT, onPrefsChanged)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(SIDEBAR_PREFS_CHANGED_EVENT, onPrefsChanged)
    }
  }, [])

  const setExpanded = useCallback((next: boolean) => {
    setExpandedState(next)
    writeSidebarExpanded(next)
    if (next) setHoverPeek(false)
  }, [])

  const setExpandOnHover = useCallback((next: boolean) => {
    setExpandOnHoverState(next)
    writeSidebarExpandOnHover(next)
    if (!next) setHoverPeek(false)
  }, [])

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded)
  }, [expanded, setExpanded])

  const onSidebarMouseEnter = useCallback(() => {
    if (canExpand && !expanded && expandOnHover) {
      setHoverPeek(true)
    }
  }, [canExpand, expanded, expandOnHover])

  const onSidebarMouseLeave = useCallback(() => {
    setHoverPeek(false)
  }, [])

  const isPinnedExpanded = canExpand && expanded
  const isHoverExpanded = canExpand && !expanded && expandOnHover && hoverPeek
  const isExpanded = isPinnedExpanded || isHoverExpanded

  return {
    isExpanded,
    isPinnedExpanded,
    canExpand,
    expanded,
    expandOnHover,
    setExpanded,
    setExpandOnHover,
    toggleExpanded,
    onSidebarMouseEnter,
    onSidebarMouseLeave,
  }
}
